import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Package, Plus, ExternalLink, Edit, Trash2, TrendingUp, Check, X, Copy, Search, Upload } from 'lucide-react';
import ProductEditDialog from './ProductEditDialog';
import ProductBulkImport from './ProductBulkImport';
import { toast } from 'sonner';
import { ImageManager } from '@/lib/imageManager';
import ProductGradeBadge from '@/components/ProductGradeBadge';
import { createSlugFromHebrew } from '@/lib/slugUtils';

interface Product {
  id: string;
  name: string;
  description: string;
  sku: string | null;
  slug: string;
  price_per_unit: number;
  cost_price?: number | null;
  measurement_unit: string;
  total_inventory: number;
  original_inventory?: number | null;
  min_quantity: number;
  max_quantity: number | null;
  step_quantity: number;
  city: string;
  supplier_name: string;
  is_active: boolean;
  images: string[];
  main_image_index?: number;
  category_id: string;
  subcategory_id: string;
  sale_type: string;
  delivery_option: string;
  fixed_delivery_cost: number | null;
  package_coverage_per_unit: number | null;
  liquidation_sale_enabled: boolean | null;
  liquidation_current_inventory: number | null;
  created_at: string;
  updated_at: string;
  categories?: { name: string };
  subcategories?: { name: string };
}

const ProductsModule = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [sortBy, setSortBy] = useState<'updated_at' | 'created_at_desc' | 'created_at_asc' | 'name' | 'price_desc' | 'price_asc'>('updated_at');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [lastEditedProductId, setLastEditedProductId] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [sortBy, filterCategory]);

  useEffect(() => {
    // Check if we're returning from product edit
    const urlParams = new URLSearchParams(window.location.search);
    const editedProductId = urlParams.get('edited');
    if (editedProductId) {
      setLastEditedProductId(editedProductId);
      // Remove the parameter from URL
      urlParams.delete('edited');
      const newUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const fetchProducts = async () => {
    try {
      // Fetch categories for filter
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, name')
        .order('name');

      setCategories(categoriesData || []);

      // Fetch products with supplier data directly (no masking)
      let query = supabase
        .from('products')
        .select('*, suppliers(name, city)');

      // Apply category filter
      if (filterCategory !== 'all') {
        query = query.eq('category_id', filterCategory);
      }

      // Apply sorting
      switch (sortBy) {
        case 'updated_at':
          query = query.order('updated_at', { ascending: false });
          break;
        case 'created_at_desc':
          query = query.order('created_at', { ascending: false });
          break;
        case 'created_at_asc':
          query = query.order('created_at', { ascending: true });
          break;
        case 'name':
          query = query.order('name', { ascending: true });
          break;
        case 'price_desc':
          query = query.order('price_per_unit', { ascending: false });
          break;
        case 'price_asc':
          query = query.order('price_per_unit', { ascending: true });
          break;
      }

      const { data, error } = await query;

      if (error) throw error;

      let sortedData = data || [];

      // If there's a last edited product, move it to the top
      if (lastEditedProductId) {
        const editedProductIndex = sortedData.findIndex(p => p.id === lastEditedProductId);
        if (editedProductIndex > -1) {
          const editedProduct = sortedData.splice(editedProductIndex, 1)[0];
          sortedData.unshift(editedProduct);
        }
      }

      // Fetch categories and subcategories for products that have them
      const categoryIds = [...new Set(sortedData.map(p => p.category_id).filter(Boolean))];
      const subcategoryIds = [...new Set(sortedData.map(p => p.subcategory_id).filter(Boolean))];

      const categoriesMap = new Map();
      const subcategoriesMap = new Map();

      if (categoryIds.length > 0) {
        const { data: categoriesData } = await supabase
          .from('categories')
          .select('id, name')
          .in('id', categoryIds);

        categoriesData?.forEach(cat => categoriesMap.set(cat.id, cat));
      }

      if (subcategoryIds.length > 0) {
        const { data: subcategoriesData } = await supabase
          .from('subcategories')
          .select('id, name')
          .in('id', subcategoryIds);

        subcategoriesData?.forEach(subcat => subcategoriesMap.set(subcat.id, subcat));
      }

      // Transform data to match expected Product interface
      const transformedData = sortedData.map(product => ({
        ...product,
        supplier_name: (product as any).suppliers?.name || product.supplier_name,
        city: (product as any).suppliers?.city || product.city,
        categories: product.category_id
          ? (categoriesMap.get(product.category_id) || { name: 'ללא קטגוריה' })
          : { name: 'ללא קטגוריה' },
        subcategories: product.subcategory_id
          ? (subcategoriesMap.get(product.subcategory_id) || { name: 'ללא תת-קטגוריה' })
          : { name: 'ללא תת-קטגוריה' }
      }));

      setProducts(transformedData);
      setTotalProducts(transformedData.length);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUnitText = (unit: string) => {
    switch (unit) {
      case 'square_meter':
        return 'מ"ר';
      case 'unit':
        return 'יח\'';
      case 'liter':
        return 'ליטר';
      case 'kilogram':
        return 'ק"ג';
      case 'package_by_square_meter':
        return 'אריזה למ"ר';
      case 'package':
        return 'אריזה';
      default:
        return unit;
    }
  };

  // Filter products by search query
  const getFilteredProducts = () => {
    if (!searchQuery.trim()) return products;
    const query = searchQuery.toLowerCase().trim();
    return products.filter(p =>
      p.name.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query) ||
      p.categories?.name?.toLowerCase().includes(query) ||
      p.subcategories?.name?.toLowerCase().includes(query) ||
      p.supplier_name?.toLowerCase().includes(query) ||
      p.city?.toLowerCase().includes(query)
    );
  };

  const filteredProducts = getFilteredProducts();

  const getActiveProducts = () => products.filter(p => p.is_active).length;
  const getInactiveProducts = () => products.filter(p => !p.is_active).length;

  const getItemValue = (p: Product, price: number) => {
    const inventory = p.liquidation_sale_enabled
      ? (p.liquidation_current_inventory ?? p.total_inventory ?? 0)
      : (p.total_inventory ?? 0);

    // Apply coverage multiplier if it exists and is relevant for the unit
    // If a product has coverage defined, the inventory count almost always represents packages/units
    const multiplier = (p.measurement_unit === 'package_by_square_meter' ||
      p.measurement_unit === 'package_by_meter' ||
      p.measurement_unit === 'square_meter' ||
      p.measurement_unit === 'meter')
      ? (p.package_coverage_per_unit || 1)
      : 1;

    return price * multiplier * inventory;
  };

  const getTotalValue = () => products.reduce((sum, p) => sum + getItemValue(p, p.price_per_unit), 0);
  const getTotalCost = () => products.reduce((sum, p) => sum + getItemValue(p, p.cost_price || 0), 0);
  const getPotentialProfit = () => getTotalValue() - getTotalCost();

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק מוצר זה?')) return;

    try {
      const { error } = await supabase.rpc('delete_product_unrestricted', {
        product_id_param: id
      });

      if (error) throw error;

      toast.success('מוצר נמחק בהצלחה');
      setSelectedProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast.error(error.message || 'שגיאה במחיקת מוצר');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) {
      toast.error('לא נבחרו מוצרים למחיקה');
      return;
    }

    if (!confirm(`האם אתה בטוח שברצונך למחוק ${selectedProducts.size} מוצרים?`)) return;

    setIsDeleting(true);
    try {
      const deletePromises = Array.from(selectedProducts).map(id =>
        supabase.rpc('delete_product_unrestricted', { product_id_param: id })
      );

      const results = await Promise.all(deletePromises);
      const errors = results.filter(r => r.error);

      if (errors.length > 0) {
        toast.error(`נכשלה מחיקת ${errors.length} מוצרים`);
      } else {
        toast.success(`${selectedProducts.size} מוצרים נמחקו בהצלחה`);
      }

      setSelectedProducts(new Set());
      fetchProducts();
    } catch (error: any) {
      console.error('Error bulk deleting products:', error);
      toast.error('שגיאה במחיקה קבוצתית');
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleProductSelection = (id: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleDuplicateProduct = async (product: Product) => {
    try {
      const newName = `${product.name} (העתק)`;
      const newSlug = createSlugFromHebrew(newName);

      const { error } = await supabase.from('products').insert({
        name: newName,
        description: product.description,
        price_per_unit: product.price_per_unit,
        original_price: (product as any).original_price ?? null,
        measurement_unit: product.measurement_unit as any,
        total_inventory: product.total_inventory,
        min_quantity: product.min_quantity,
        max_quantity: product.max_quantity,
        step_quantity: product.step_quantity,
        city: product.city,
        supplier_id: (product as any).supplier_id ?? null,
        supplier_name: product.supplier_name,
        is_active: false,
        images: product.images,
        main_image_index: product.main_image_index ?? 0,
        category_id: product.category_id,
        subcategory_id: product.subcategory_id,
        sale_type: product.sale_type as any,
        delivery_option: product.delivery_option as any,
        fixed_delivery_cost: product.fixed_delivery_cost,
        shipping_type: (product as any).shipping_type ?? 'fixed',
        shipping_units_per_cost: (product as any).shipping_units_per_cost ?? 1,
        shipping_cost_per_group: (product as any).shipping_cost_per_group ?? null,
        package_coverage_per_unit: (product as any).package_coverage_per_unit ?? null,
        grade: (product as any).grade ?? 'B',
        color_family: (product as any).color_family ?? null,
        material: (product as any).material ?? null,
        finish: (product as any).finish ?? null,
        vendor_location: (product as any).vendor_location ?? null,
        slug: newSlug,
      });

      if (error) throw error;

      toast.success('מוצר שוכפל בהצלחה ונשמר כלא פעיל');
      fetchProducts();
    } catch (error: any) {
      console.error('Error duplicating product:', error);
      toast.error(error.message || 'שגיאה בשכפול מוצר');
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען מוצרים...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">סה"כ מוצרים</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{totalProducts}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">מוצרים פעילים</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{getActiveProducts()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">מוצרים לא פעילים</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{getInactiveProducts()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">ערך כולל במלאי</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              ₪{getTotalValue().toLocaleString()}
            </div>
            <div className="flex flex-col gap-1 mt-2 text-xs text-muted-foreground">
              <div className="flex justify-between">
                <span>עלות כוללת:</span>
                <span>₪{getTotalCost().toLocaleString()}</span>
              </div>
              <div className="flex justify-between font-medium text-green-600">
                <span>רווח צפוי:</span>
                <span>₪{getPotentialProfit().toLocaleString()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>


      {/* Products Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              רשימת מוצרים
              {selectedProducts.size > 0 && (
                <Badge variant="secondary">
                  {selectedProducts.size} נבחרו
                </Badge>
              )}
            </CardTitle>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
              {selectedProducts.size > 0 && (
                <Button
                  variant="destructive"
                  className="gap-2 w-full sm:w-auto"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4" />
                  מחק {selectedProducts.size} מוצרים
                </Button>
              )}
              <Button
                variant="outline"
                className="gap-2 w-full sm:w-auto"
                onClick={() => setShowImportDialog(true)}
              >
                <Upload className="h-4 w-4" />
                ייבוא ממסמך Excel
              </Button>
              <div className="relative w-full sm:w-64">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="חיפוש מוצר..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pr-10 pl-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background text-sm w-full sm:w-auto min-w-0"
              >
                <option value="all">כל הקטגוריות</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 border rounded-md bg-background text-sm w-full sm:w-auto min-w-0"
              >
                <option value="updated_at">לפי עדכון אחרון</option>
                <option value="created_at_desc">החדשים ביותר</option>
                <option value="created_at_asc">הישנים ביותר</option>
                <option value="name">לפי שם</option>
                <option value="price_desc">מחיר מגבוה לנמוך</option>
                <option value="price_asc">מחיר מנמוך לגבוה</option>
              </select>
              <Button
                className="gap-2 w-full sm:w-auto"
                onClick={() => navigate('/admin/product/create')}
              >
                <Plus className="h-4 w-4" />
                הוסף מוצר
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-center w-12 px-4 whitespace-nowrap">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={selectedProducts.size === filteredProducts.length && filteredProducts.length > 0}
                          onCheckedChange={toggleSelectAll}
                        />
                      </div>
                    </TableHead>
                    <TableHead className="text-right whitespace-nowrap">תמונה</TableHead>
                    <TableHead className="text-right whitespace-nowrap">מקט פנימי</TableHead>
                    <TableHead className="text-right whitespace-nowrap">שם המוצר</TableHead>
                    <TableHead className="text-right whitespace-nowrap">קטגוריה</TableHead>
                    <TableHead className="text-right whitespace-nowrap">דירוג</TableHead>
                    <TableHead className="text-right whitespace-nowrap">סוג מכירה</TableHead>
                    <TableHead className="text-right whitespace-nowrap">מחיר</TableHead>
                    <TableHead className="text-right whitespace-nowrap">מלאי</TableHead>
                    <TableHead className="text-right whitespace-nowrap">ערך במלאי</TableHead>
                    <TableHead className="text-right whitespace-nowrap">ספק</TableHead>
                    <TableHead className="text-right whitespace-nowrap">תאריך יצירה</TableHead>
                    <TableHead className="text-right whitespace-nowrap">עדכון אחרון</TableHead>
                    <TableHead className="text-right whitespace-nowrap">סטטוס</TableHead>
                    <TableHead className="text-right whitespace-nowrap">פעולות</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.id}>
                      <TableCell className="text-center px-4">
                        <div className="flex justify-center">
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={() => toggleProductSelection(product.id)}
                          />
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={ImageManager.getImageUrl(product.images[product.main_image_index || 0] || product.images[0])}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                            onError={(e) => ImageManager.handleImageError(e)}
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                            <Package className="h-6 w-6 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {product.sku || '-'}
                      </TableCell>
                      <TableCell className="font-medium max-w-48 text-right">
                        <div
                          className="truncate cursor-pointer hover:text-primary transition-colors"
                          onClick={() => navigate(`/admin/product/edit/${product.id}`)}
                        >
                          {product.name}
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {product.description}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="space-y-1">
                          <div className="text-sm font-medium">
                            {product.categories?.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {product.subcategories?.name}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <ProductGradeBadge grade={(product as any).grade || 'B'} size="sm" />
                      </TableCell>
                      <TableCell className="text-right">
                        {(product as any).liquidation_sale_enabled ? (
                          <Badge variant="outline" className="bg-white text-green-700 border-green-200 whitespace-nowrap">
                            מכירת חיסול
                          </Badge>
                        ) : (product as any).group_buy_enabled ? (
                          <div className="space-y-1">
                            <Badge variant="outline" className="bg-white text-orange-700 border-orange-200 whitespace-nowrap">
                              קבוצת רכישה
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              {Number((product as any).group_buy_current_quantity || 0).toFixed(2)} / {Number((product as any).group_buy_target_quantity || 0).toFixed(2)} {getUnitText(product.measurement_unit)}
                            </div>
                          </div>
                        ) : (
                          <Badge variant="outline" className="bg-white text-blue-700 border-blue-200 whitespace-nowrap">
                            רגיל
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium">
                          ₪{product.price_per_unit}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ל{getUnitText(product.measurement_unit)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {(product as any).group_buy_enabled ? (
                          <div className="text-sm text-muted-foreground">ללא הגבלה</div>
                        ) : (
                          <>
                            <div className="font-medium whitespace-nowrap">
                              {product.liquidation_sale_enabled ? (product.liquidation_current_inventory ?? 0) : product.total_inventory} {getUnitText(product.measurement_unit)}
                              {product.original_inventory != null && !product.liquidation_sale_enabled && (
                                <span className="text-xs text-muted-foreground mr-1">
                                  / {product.original_inventory}
                                </span>
                              )}
                            </div>
                            {product.package_coverage_per_unit && product.package_coverage_per_unit !== 1 && (
                              <div className="text-xs text-blue-600 font-medium">
                                סה"כ: {((product.liquidation_sale_enabled ? (product.liquidation_current_inventory ?? 0) : product.total_inventory) * product.package_coverage_per_unit).toFixed(2)} {product.measurement_unit.includes('meter') ? 'מטר' : 'מ"ר'}
                              </div>
                            )}
                            <div className="text-xs text-muted-foreground">
                              מינימום: {product.min_quantity}
                            </div>
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="font-medium text-primary">
                          ₪{getItemValue(product, product.price_per_unit).toLocaleString()}
                        </div>
                        <div className="flex flex-col gap-0.5 mt-1 text-[10px] text-muted-foreground whitespace-nowrap">
                          <div className="flex justify-between gap-2">
                            <span>עלות:</span>
                            <span>₪{getItemValue(product, product.cost_price || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between gap-2 font-medium text-green-600">
                            <span>רווח:</span>
                            <span>₪{(getItemValue(product, product.price_per_unit) - getItemValue(product, product.cost_price || 0)).toLocaleString()}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="max-w-32 truncate">{product.supplier_name}</div>
                        <div className="text-xs text-muted-foreground">{product.city}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-xs text-muted-foreground">
                          {new Date(product.created_at).toLocaleDateString('he-IL')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="text-xs text-muted-foreground">
                          {new Date(product.updated_at).toLocaleDateString('he-IL')}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end">
                          {product.is_active ? (
                            <Check className="h-5 w-5 text-green-600" />
                          ) : (
                            <X className="h-5 w-5 text-red-600" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/product/${product.slug}`, '_blank')}
                            title="הצג בחנות"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDuplicateProduct(product)}
                            title="שכפל מוצר"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/admin/product/edit/${product.id}`)}
                            title="ערוך מוצר"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleDeleteProduct(product.id)}
                            title="מחק מוצר"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </CardContent>
      </Card>

      <ProductBulkImport
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={() => {
          fetchProducts();
          setShowImportDialog(false);
        }}
      />
    </div>
  );
};

export default ProductsModule;